/**
 * Public contact: direct email links (no form).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, MessageSquare, ShieldCheck } from 'lucide-react';
import ShieldIcon from './ShieldIcon';

const TEAM_CONTACTS = [
    {
        name: 'Matthew Ma',
        email: 'matthewma003@gmail.com',
    },
    {
        name: 'Matthew Yijie Lu',
        email: 'matthewyijielu0317@gmail.com',
    },
    {
        name: 'Haonan Guo',
        email: 'guohaonan980421@gmail.com',
    },
] as const;

const ContactPage: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-brand-50/60 px-4 py-12 text-secondary-900 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
                <div className="mb-8">
                    <Link
                        to="/"
                        className="mb-6 inline-flex items-center text-secondary-600 transition-colors hover:text-brand-500"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Link>
                    <div className="mb-4 flex items-center">
                        <span className="mr-4 grid h-11 w-11 shrink-0 overflow-hidden rounded-full bg-brand-500 shadow-sm ring-2 ring-brand-500/25 sm:h-12 sm:w-12">
                            <ShieldIcon className="size-full" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-600">
                                {t('contact.eyebrow')}
                            </p>
                            <h1 className="mt-2 text-3xl font-bold tracking-tight text-secondary-900 sm:text-4xl">
                                {t('contact.pageTitle')}
                            </h1>
                        </div>
                    </div>
                    <p className="max-w-[65ch] text-base leading-[1.65] text-secondary-600 sm:text-lg">
                        {t('contact.pageSubtitle')}
                    </p>
                </div>

                <div className="grid gap-5 lg:grid-cols-[1fr_0.78fr] lg:items-start">
                    <section className="rounded-2xl border border-secondary-200/90 bg-white p-6 shadow-xl shadow-secondary-900/[0.06] sm:p-8">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/12 text-brand-600">
                                <MessageSquare className="h-5 w-5" aria-hidden />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold tracking-tight text-secondary-900">
                                    {t('contact.teamHeading')}
                                </h2>
                                <p className="mt-2 text-sm leading-relaxed text-secondary-600">
                                    {t('contact.directEmailIntro')}
                                </p>
                            </div>
                        </div>

                        <ul className="mt-7 space-y-3">
                            {TEAM_CONTACTS.map((person) => (
                                <li key={person.email}>
                                    <a
                                        href={`mailto:${person.email}`}
                                        className="group flex items-center justify-between gap-4 rounded-xl border border-secondary-200 bg-secondary-50/80 px-4 py-4 text-left transition-[background-color,border-color,box-shadow] duration-200 ease-out hover:border-brand-300 hover:bg-brand-50/70 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/70 focus-visible:ring-offset-2"
                                    >
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-secondary-900">{person.name}</span>
                                            <span className="mt-1 block break-all text-sm text-secondary-600">{person.email}</span>
                                        </span>
                                        <Mail
                                            className="h-4 w-4 shrink-0 text-brand-600 transition-transform duration-200 ease-out group-hover:-translate-y-0.5"
                                            aria-hidden
                                        />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <aside className="rounded-2xl border border-brand-200/70 bg-brand-50/70 p-6 shadow-sm sm:p-7">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm ring-1 ring-brand-200/80">
                            <ShieldCheck className="h-5 w-5" aria-hidden />
                        </div>
                        <h2 className="mt-5 text-lg font-semibold tracking-tight text-secondary-900">
                            {t('contact.responseHeading')}
                        </h2>
                        <p className="mt-3 text-sm leading-relaxed text-secondary-700">{t('contact.responseBody')}</p>
                        <p className="mt-5 text-xs leading-relaxed text-secondary-500">
                            {t('contact.privacyNote')}{' '}
                            <Link to="/privacy" className="font-medium text-brand-600 underline-offset-2 hover:underline">
                                {t('contact.privacyLink')}
                            </Link>
                            .
                        </p>
                    </aside>
                </div>

                <div className="mt-8 rounded-2xl border border-secondary-200 bg-white/80 p-5 text-sm leading-relaxed text-secondary-600 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-6">
                    <p>{t('contact.supportNote')}</p>
                    <Link
                        to="/try"
                        className="mt-4 inline-flex shrink-0 items-center justify-center rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-brand-50 shadow-sm shadow-brand-500/20 transition-colors hover:bg-brand-600 sm:mt-0"
                    >
                        {t('contact.tryProduct')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
